import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Moon, RotateCcw, Sun } from 'lucide-react'

import {
  THEME_COLORS,
  DEFAULT_THEME_KEY,
  applyThemeColor,
  applyThemeMode,
  getSavedThemeColor,
  getSavedThemeMode,
  computeThemeVars,
  onThemeColorChange,
  onThemeModeChange,
  type ThemeColor,
} from '@lib/theme'
import { Switch } from '@components/ui/switch'
import { cn } from '@lib/utils'

const SPECIALTY_THEMES = THEME_COLORS

// Turn the engine's var map into an inline-style object of CSS custom properties,
// so a wrapper can host a fully-themed subtree without touching the global root.
function themeStyle(theme: ThemeColor, isDark: boolean): React.CSSProperties {
  return computeThemeVars(theme, isDark) as React.CSSProperties
}

// Pick black/white for legible text on an arbitrary accent fill (preview only).
function readableOn(rgb: string): string {
  const [r, g, b] = rgb.split(/\s+/).map(Number)
  const lin = (c: number) => {
    const x = c / 255
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
  }
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  return lum > 0.4 ? '#0a1014' : '#ffffff'
}

// A scoped mini-mockup rendered entirely from `theme`'s tokens — a card, a gradient
// primary button, an accent secondary button, a focused input, and dimmed text.
// Nothing here leaks to the global theme; it's all driven by the wrapper's vars.
function ThemePreview({ theme, isDark }: { theme: ThemeColor; isDark: boolean }) {
  const { t } = useTranslation()
  const accent = theme.complement // the theme's accent channel
  return (
    <div
      style={themeStyle(theme, isDark)}
      className="rounded-xl border border-ml-outline-variant/40 bg-ml-surface p-4"
    >
      <div className="rounded-lg border border-ml-outline-variant/30 bg-ml-surface-container-lowest p-4 shadow-sm">
        <p className="text-sm font-semibold text-ml-on-surface">{t('settings.preview_card_title')}</p>
        <p className="mt-1 text-xs text-ml-on-surface-variant">{t('settings.preview_card_dimmed')}</p>

        <div className="mt-3 flex gap-2">
          <span className="gradient-medical inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold text-white shadow-sm">
            {t('settings.preview_primary')}
          </span>
          <span
            className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm"
            style={{ backgroundColor: `rgb(${accent})`, color: readableOn(accent) }}
          >
            {t('settings.preview_secondary')}
          </span>
        </div>

        {/* Input shown in its focused state (ring + primary border) */}
        <div className="mt-3">
          <div className="flex h-8 items-center rounded-md border-2 border-ml-primary bg-ml-surface px-2 ring-2 ring-ml-primary/25">
            <span className="text-xs text-ml-on-surface-variant">{t('settings.preview_input')}</span>
            <span className="ml-0.5 h-3.5 w-px animate-pulse bg-ml-primary" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  )
}

// Palette thumbnail: primary · gradient · accent · background chips.
function PaletteThumb({ theme }: { theme: ThemeColor }) {
  const chips: Array<[string, string]> = [
    ['primary', theme.primary],
    ['gradient', theme.gradient ?? theme.primary],
    ['accent', theme.complement],
    ['background', theme.background ?? '255 255 255'],
  ]
  return (
    <div className="flex overflow-hidden rounded-md ring-1 ring-black/10" aria-hidden>
      {chips.map(([name, rgb]) => (
        <span key={name} className="h-6 w-6" style={{ backgroundColor: `rgb(${rgb})` }} />
      ))}
    </div>
  )
}

// Cards selector with a live preview panel. Selection instant-applies (matching the
// app's existing behavior); hover/focus only drives the preview, never the global theme.
export function SpecialtyThemePicker() {
  const { t } = useTranslation()
  const [selected, setSelected] = useState(getSavedThemeColor)
  const [isDark, setIsDark] = useState(() => getSavedThemeMode() === 'dark')
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [focusIndex, setFocusIndex] = useState(0)
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => onThemeColorChange(setSelected), [])
  useEffect(() => onThemeModeChange((m) => setIsDark(m === 'dark')), [])

  const previewTheme =
    SPECIALTY_THEMES.find((theme) => theme.key === (previewKey ?? selected)) ?? SPECIALTY_THEMES[0]

  const select = (key: string) => {
    applyThemeColor(key, { animate: true })
    setSelected(key)
  }

  // Roving-tabindex arrow navigation across the radio cards.
  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = SPECIALTY_THEMES.length - 1
    let next = index
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = index === last ? 0 : index + 1
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = index === 0 ? last : index - 1
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = last
    else return
    e.preventDefault()
    setFocusIndex(next)
    setPreviewKey(SPECIALTY_THEMES[next].key)
    cardRefs.current[next]?.focus()
  }

  return (
    <section aria-label={t('settings.theme_specialty')} className="space-y-4">
      {/* Header row: title + dark-mode toggle + reset */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ml-on-surface">{t('settings.theme_specialty')}</p>
          <p className="text-xs text-ml-on-surface-variant/70">{t('settings.theme_specialty_desc')}</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-ml-on-surface-variant">
          {isDark ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
          <span>{t('settings.dark_mode')}</span>
          <Switch
            checked={isDark}
            onCheckedChange={(v) => {
              applyThemeMode(v ? 'dark' : 'light', { animate: true })
              setIsDark(v)
            }}
            aria-label={t('settings.dark_mode')}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            applyThemeMode('light', { animate: true })
            applyThemeColor(DEFAULT_THEME_KEY, { animate: true })
            setSelected(DEFAULT_THEME_KEY)
            setIsDark(false)
            setPreviewKey(null)
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ml-outline-variant/30 px-2.5 py-1.5 text-xs font-medium text-ml-on-surface-variant transition-colors hover:border-ml-outline-variant/50 hover:text-ml-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ml-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ml-surface-container-low"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('settings.theme_reset')}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        {/* Cards */}
        <div
          role="radiogroup"
          aria-label={t('settings.theme_specialty')}
          className="grid gap-3 sm:grid-cols-2"
          onMouseLeave={() => setPreviewKey(null)}
        >
          {SPECIALTY_THEMES.map((theme, i) => {
            const isSelected = selected === theme.key
            return (
              <button
                key={theme.key}
                ref={(el) => (cardRefs.current[i] = el)}
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={i === focusIndex ? 0 : -1}
                onClick={() => select(theme.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    select(theme.key)
                  } else onKeyDown(e, i)
                }}
                onMouseEnter={() => setPreviewKey(theme.key)}
                onFocus={() => {
                  setFocusIndex(i)
                  setPreviewKey(theme.key)
                }}
                className={cn(
                  'group relative flex flex-col gap-2.5 rounded-xl border-2 bg-ml-surface-container-lowest p-3 text-left transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ml-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ml-surface-container-low',
                  isSelected
                    ? 'border-ml-primary shadow-md'
                    : 'border-ml-outline-variant/25 hover:border-ml-outline-variant/50 hover:shadow-sm',
                )}
              >
                <div className="flex items-center justify-between">
                  <PaletteThumb theme={theme} />
                  <span
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full transition-colors',
                      isSelected ? 'bg-ml-primary text-white' : 'bg-ml-outline-variant/20 text-transparent',
                    )}
                    aria-hidden
                  >
                    <Check className="h-3 w-3" />
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-ml-on-surface">{t(`settings.theme_${theme.key}_name`)}</p>
                  <p className="text-xs text-ml-on-surface-variant/70">{t(`settings.theme_${theme.key}_desc`)}</p>
                </div>
              </button>
            )
          })}
        </div>

        {/* Live preview */}
        <div className="flex flex-col gap-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">
            {t('settings.theme_preview')} · {t(`settings.theme_${previewTheme.key}_name`)}
          </p>
          <ThemePreview theme={previewTheme} isDark={isDark} />
        </div>
      </div>
    </section>
  )
}
